/**
 * Chart-type trace keys and helpers for spectrum/waveform overlay comparison.
 */

export const OVERLAY_TRACE_KEYS = {
  SPECTRUM: 'spectrum_data',
  WAVEFORM: 'waveform_data'
};

export const OVERLAY_PERSISTENT_CHART_TYPES = ['trend', 'spectrum', 'waveform', 'TSW'];

export const OVERLAY_TOGGLE_CHART_TYPES = ['spectrum', 'waveform'];

/**
 * @param {string} chartType
 * @param {string} [tagChartType] - TagChart type prop (spectrum, demod_spectrum, waveform)
 * @returns {'spectrum_data'|'waveform_data'|null}
 */
export const getOverlayDataKey = (chartType, tagChartType) => {
  const type = tagChartType || chartType;
  if (type === 'waveform') {
    return OVERLAY_TRACE_KEYS.WAVEFORM;
  }
  if (type === 'spectrum' || type === 'demod_spectrum') {
    return OVERLAY_TRACE_KEYS.SPECTRUM;
  }
  if (chartType === 'spectrum' || chartType === 'TSW') {
    return OVERLAY_TRACE_KEYS.SPECTRUM;
  }
  if (chartType === 'waveform') {
    return OVERLAY_TRACE_KEYS.WAVEFORM;
  }
  return null;
};

/**
 * @param {string} chartType
 * @returns {Array<'spectrum_data'|'waveform_data'>}
 */
export const getOverlayTraceKeysForChartType = (chartType) => {
  if (chartType === 'TSW') {
    return [OVERLAY_TRACE_KEYS.SPECTRUM, OVERLAY_TRACE_KEYS.WAVEFORM];
  }
  if (chartType === 'waveform') {
    return [OVERLAY_TRACE_KEYS.WAVEFORM];
  }
  if (chartType === 'spectrum') {
    return [OVERLAY_TRACE_KEYS.SPECTRUM];
  }
  return [];
};

export const pointHasTraceData = (point, dataKey) => {
  if (!point || !dataKey) return false;
  const data = point[dataKey];
  return Array.isArray(data) && data.length > 0;
};

export const getPointsWithTraceData = (points, dataKey) => {
  if (!points || !dataKey) return [];
  return points.filter(p => pointHasTraceData(p, dataKey));
};

export const isOverlayPersistentChartType = chartType =>
  OVERLAY_PERSISTENT_CHART_TYPES.indexOf(chartType) !== -1;

// A valid-but-empty response (no error, zero-length data) is intentionally treated
// as an error -> Retry. Distinguishing a true empty state is a pending product decision.
export const isWaveformResponseError = (response) => {
  if (!response) return true;
  if (Array.isArray(response.waveform_data) && response.waveform_data.length > 0) return false;
  if (response.message || response.error) return true;
  return !Array.isArray(response.waveform_data) || response.waveform_data.length === 0;
};

export const isSpectrumResponseError = (response) => {
  if (!response) return true;
  if (Array.isArray(response.spectrum_data) && response.spectrum_data.length > 0) return false;
  if (response.message || response.error) return true;
  return !Array.isArray(response.spectrum_data) || response.spectrum_data.length === 0;
};

/**
 * Waveform duration in ms from sampling metadata.
 * @param {Object} pointOrResponse
 * @returns {number|null}
 */
export const getOverlayTraceTypeForDataKey = dataKey =>
  (dataKey === OVERLAY_TRACE_KEYS.WAVEFORM ? 'waveform' : 'spectrum');

export const getPointOverlayTraceStatus = (point, dataKey) => {
  if (!point || !point.traceStatus) return 'idle';
  const traceType = getOverlayTraceTypeForDataKey(dataKey);
  return point.traceStatus[traceType] || 'idle';
};

export const hasOverlayTracesLoading = (points, dataKey) => {
  if (!points || !dataKey) return false;
  return points.some(point => getPointOverlayTraceStatus(point, dataKey) === 'loading');
};

export const hasOverlayTracesError = (points, dataKey) => {
  if (!points || !dataKey) return false;
  return points.some(point => getPointOverlayTraceStatus(point, dataKey) === 'error');
};

export const getWaveformDurationMs = (pointOrResponse) => {
  if (!pointOrResponse) return null;
  const data = pointOrResponse.waveform_data;
  const samplingRate = pointOrResponse.sampling_rate;
  if (Array.isArray(data) && data.length > 0 && data[data.length - 1].x != null) {
    return data[data.length - 1].x;
  }
  if (Array.isArray(data) && samplingRate && data.length > 0) {
    return (1000 / samplingRate) * (data.length - 1);
  }
  return null;
};
